/**
 * apiClient — authenticated fetch wrapper with automatic token refresh.
 *
 * All service functions should use `apiFetch` instead of calling `fetch` directly.
 * The client:
 *   1. Injects the Authorization header from localStorage automatically.
 *   2. On a 401 response, attempts a silent token refresh (POST /api/refresh).
 *   3. Retries the original request once with the new token.
 *   4. If the refresh also fails (expired/revoked), clears auth state and
 *      redirects the user to /login?redirect=<current-path>.
 *
 * A module-level promise guard ensures only one refresh request is in flight
 * at a time even if multiple requests 401 simultaneously.
 */

export class AuthExpiredError extends Error {
  constructor() {
    super("Session expired — please log in again.");
    this.name = "AuthExpiredError";
  }
}

export class PaywallError extends Error {
  constructor(
    public resource: string,
    public limit: number
  ) {
    super(`Free tier limit reached for ${resource} (${limit}).`);
    this.name = "PaywallError";
  }
}

/**
 * ApiError preserves the HTTP status and the raw body so callers can branch on a
 * status instead of string-parsing a message. It still extends Error and keeps
 * the historical `HTTP <status>: <body>` message, so existing catch blocks are
 * unaffected.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string
  ) {
    super(`HTTP ${status}: ${body}`);
    this.name = "ApiError";
  }
}

/**
 * A marketplace bundle needs to be purchased. Deliberately distinct from
 * PaywallError: that one is the free-tier creation limit and opens the upgrade
 * modal, which is the wrong prompt here.
 */
export class PurchaseRequiredError extends Error {
  constructor(
    public listingId: number,
    public priceCents: number,
    public currency: string
  ) {
    // The fields are named in the message on purpose: the base eslint
    // no-unused-vars cannot see parameter-property assignments, and a logged
    // "requires a purchase" with no listing id is useless anyway.
    super(`Listing ${listingId} requires a purchase (${priceCents} ${currency}).`);
    this.name = "PurchaseRequiredError";
  }
}

// In-flight refresh promise — shared across all concurrent callers *within this
// tab* so the backend (which revokes the refresh token on every use) sees one
// rotation per burst. Cross-tab bursts are serialized separately via the Web
// Locks API below. This is the single source of truth for token refresh;
// AuthProvider.refresh delegates here too.
let refreshPromise: Promise<string | null> | null = null;

/**
 * Perform the actual token exchange. Assumes the caller holds the cross-tab
 * lock. First re-checks storage: if another tab already rotated the refresh
 * token while we were waiting for the lock, the stored access token is fresh —
 * return it instead of replaying the now-revoked token against /api/refresh.
 */
async function rotateToken(refreshTokenBeforeLock: string): Promise<string | null> {
  const current = localStorage.getItem("refreshToken");
  if (current && current !== refreshTokenBeforeLock) {
    // Another tab beat us to it; adopt the token it just stored.
    return localStorage.getItem("authToken");
  }
  if (!current) return null;

  try {
    const res = await fetch("/api/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: current }),
    });

    if (!res.ok) return null;

    const data: { access_token: string; refresh_token: string } = await res.json();
    localStorage.setItem("authToken", data.access_token);
    localStorage.setItem("refreshToken", data.refresh_token);
    return data.access_token;
  } catch {
    return null;
  }
}

/**
 * Single-flight refresh: exchange the stored refresh token for a new access token,
 * updating localStorage on success. Returns the new access token, or null on
 * failure (no side-effect redirect — callers decide how to handle null).
 *
 * Serialized across browser tabs of the same origin via the Web Locks API so two
 * tabs that 401 at the same time don't both spend (and mutually revoke) the same
 * refresh token. Falls back to tab-local single-flight where locks are unavailable.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshTokenBeforeLock = localStorage.getItem("refreshToken");
    if (!refreshTokenBeforeLock) return null;

    if (typeof navigator !== "undefined" && "locks" in navigator) {
      return navigator.locks.request("auth-token-refresh", () =>
        rotateToken(refreshTokenBeforeLock)
      );
    }
    return rotateToken(refreshTokenBeforeLock);
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Attempt a refresh for the apiFetch path; clears auth storage and redirects
 * to /login on failure.
 */
async function attemptRefresh(): Promise<string | null> {
  const token = await refreshAccessToken();
  if (!token) clearAuthAndRedirect();
  return token;
}

function clearAuthAndRedirect(): void {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/login?redirect=${redirect}`;
}

/**
 * Authenticated fetch. Reads the access token from localStorage automatically.
 * Handles 401 → refresh → retry transparently.
 */
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem("authToken");

  const buildHeaders = (t: string | null): HeadersInit => ({
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
    // Allow callers to override Content-Type (e.g. for multipart uploads)
    ...(options.headers ?? {}),
  });

  let response = await fetch(url, { ...options, headers: buildHeaders(token) });

  if (response.status === 401) {
    const newToken = await attemptRefresh();
    if (!newToken) {
      throw new AuthExpiredError();
    }
    // Retry once with the refreshed token
    response = await fetch(url, { ...options, headers: buildHeaders(newToken) });
  }

  if (response.status === 402) {
    const body = await response
      .clone()
      .json()
      .catch(() => null);
    if (body && body.error === "paywall") {
      throw new PaywallError(body.resource, body.limit);
    }
    if (body && body.error === "purchase_required") {
      throw new PurchaseRequiredError(body.listingId, body.priceCents, body.currency);
    }
  }

  return response;
}

/**
 * Convenience helper — resolves to the parsed JSON body or throws on non-2xx.
 */
export async function apiFetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await apiFetch(url, options);
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }
  return res.json() as Promise<T>;
}
